<h2 mat-dialog-title>{{ 'CALLS.TIMELINE.TITLE' | translate }}</h2>
<mat-dialog-content class="timeline-dialog-content">
  <mat-divider></mat-divider>

  @if (timeline$ | async; as result) {
    @if (result.isLoading) {
      <div class="loading-state">
        <mat-spinner diameter="40"></mat-spinner>
        <p>{{ 'CALLS.TIMELINE.LOADING' | translate }}</p>
      </div>
    } @else if (result.isError) {
      <div class="error-state">
        <mat-icon>error</mat-icon>
        <p>{{ 'CALLS.TIMELINE.ERROR' | translate }}</p>
      </div>
    } @else {
      @if (result.data; as data) {
        <div class="timeline-body">
          <div class="timeline-section">
            <div class="timeline-header">
              <h3>{{ 'CALLS.TIMELINE.ACTIVITY_LOG' | translate }}</h3>
              <button
                mat-icon-button
                [matTooltip]="sortAscending ? ('CALLS.TIMELINE.SORT_DESC' | translate) : ('CALLS.TIMELINE.SORT_ASC' | translate)"
                (click)="toggleSort()"
              >
                <mat-icon>{{ sortAscending ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
              </button>
            </div>

            <div class="timeline">
              @if (groups$ | async; as groups) {
                @for (group of sortGroups(groups); track group; let isLast = $last) {
                  @if (group.events[0]; as event) {
                    <div class="timeline-event" [attr.data-event-type]="event.type">
                      <div class="timeline-marker" [class]="'marker-' + getEventColor(group.type)">
                        <mat-icon>{{ getEventIcon(group.type) }}</mat-icon>
                      </div>

                      @if (!isLast) {
                        <div class="timeline-line"></div>
                      }

                      <div class="timeline-content">
                        <div class="event-title">
                          @if (event.type === 'SHARE_MEETING_WITH_EXISTING_USER' || event.type === 'SHARE_MEETING_WITH_NEW_USER') {
                            @if (event.metadata?.sharedUserName) {
                              <b>{{ 'CALLS.TIMELINE.METRIC_TYPES.' + event.type | translate: { sharedUserName: event.metadata.sharedUserName } }}</b>
                            } @else {
                              <b>{{ 'CALLS.TIMELINE.METRIC_TYPES.' + event.type | translate: { sharedUserName: 'user' } }}</b>
                            }
                          } @else {
                            <b>{{ 'CALLS.TIMELINE.METRIC_TYPES.' + group.type | translate }}</b>
                          }
                        </div>

                        <div class="event-details">
                          @if (event.userName) {
                            <span class="user-name">{{ event.userName }}</span>
                          }
                          @if (event.metadata !== null) {
                            @if (event.type === 'UPLOAD_RESOURCE' && event.metadata.resource !== undefined) {
                              <span class="additional-details">{{
                                'CALLS.CALL_INFO.DOCUMENT_UPLOAD.' + event.metadata.resource.toUpperCase() | translate
                              }}</span>
                            } @else if (
                              event.type === 'DOWNLOAD_TRANSCRIPT' ||
                              event.type === 'DOWNLOAD_MINUTES' ||
                              event.type === 'DOWNLOAD_OUTLINE' ||
                              event.type === 'DOWNLOAD_ACTION_ITEMS'
                            ) {
                              <span class="additional-details">{{ event.metadata.extension }}</span>
                            } @else if (event.type === 'ADJUST_RETENTION_PERIOD' && event.metadata.retentionPeriodStartDate !== undefined) {
                              <span class="additional-details">
                                {{
                                  'CALLS.TIMELINE.METRIC_TYPES.ADJUST_RETENTION_PERIOD_DETAILS'
                                    | translate: { retentionPeriodStartDate: event.metadata.retentionPeriodStartDate | date: 'd MMM y' }
                                }}
                              </span>
                            }
                          }
                          @if (group.detailEvents.length > 0) {
                            <button type="button" class="session-toggle" [attr.aria-expanded]="isExpanded(group)" (click)="toggleGroup(group)">
                              {{ changeCountKey(group) | translate: { count: group.detailEvents.length } }}
                              <mat-icon>{{ isExpanded(group) ? 'expand_less' : 'expand_more' }}</mat-icon>
                            </button>
                          }
                        </div>

                        @if (isExpanded(group)) {
                          <div class="session-changes">
                            @for (detail of group.detailEvents; track detail) {
                              <div class="session-change">
                                <mat-icon>{{ getEventIcon(detail.type) }}</mat-icon>
                                <span class="session-change-text">
                                  {{ 'CALLS.TIMELINE.METRIC_TYPES.' + detail.type | translate: detail.metadata }}
                                  @if (detail.metadata?.changes?.length) {
                                    <span class="additional-details">
                                      @for (fieldChange of detail.metadata.changes; track fieldChange; let isLastChange = $last) {
                                        {{ fieldChangeKey(fieldChange) | translate: fieldChange }}{{ isLastChange ? '' : ', ' }}
                                      }
                                    </span>
                                  }
                                </span>
                                <span class="session-change-time">{{ detail.timestamp | date: 'HH:mm:ss' }}</span>
                              </div>
                            }
                          </div>
                        }

                        <div class="event-timestamp">
                          {{ group.lastTimestamp | date: 'd MMM y HH:mm' }}
                        </div>
                      </div>
                    </div>
                  }
                }
              }
            </div>
          </div>

          <div class="summary-section">
            <div class="summary-card">
              <h3>
                <mat-icon>toggle_on</mat-icon>
                {{ 'CALLS.TIMELINE.USERS_ENABLED_ASSISTANT' | translate }}
              </h3>

              <div class="user-list">
                @if (getUsersWhoEnabledAssistant(data).length > 0) {
                  @for (user of getUsersWhoEnabledAssistant(data); track user.userName) {
                    <div class="user-item">
                      <app-avatar [input]="user.userName" size="small"></app-avatar>
                      <div class="user-info">
                        <div class="user-name">{{ user.userName }}</div>
                        <div class="user-meta">
                          @if (user.lastEnabledAt) {
                            <span class="last-viewed">{{ 'CALLS.TIMELINE.LAST_ENABLED' | translate }}: {{ user.lastEnabledAt | date: 'd MMM y HH:mm' }} </span>
                          }
                        </div>
                      </div>
                    </div>
                  }
                } @else {
                  <div class="empty-state">
                    {{ 'CALLS.TIMELINE.NO_USERS_ENABLED' | translate }}
                  </div>
                }
              </div>
            </div>

            <div class="summary-card">
              <h3>
                <mat-icon>visibility</mat-icon>
                {{ 'CALLS.TIMELINE.USERS_VIEWED' | translate }}
              </h3>

              <div class="user-list">
                @if (getUsersWhoViewed(data).length > 0) {
                  @for (user of getUsersWhoViewed(data); track user.userName) {
                    <div class="user-item">
                      <app-avatar [input]="user.userName" size="small"></app-avatar>
                      <div class="user-info">
                        <div class="user-name">{{ user.userName }}</div>
                        <div class="user-meta">
                          {{ 'CALLS.TIMELINE.VIEW_COUNT' | translate: { count: user.viewCount } }}
                          @if (user.lastViewedAt) {
                            <span class="last-viewed"> · {{ 'CALLS.TIMELINE.LAST_VIEWED' | translate }}: {{ user.lastViewedAt | date: 'd MMM y HH:mm' }} </span>
                          }
                        </div>
                      </div>
                    </div>
                  }
                } @else {
                  <div class="empty-state">
                    {{ 'CALLS.TIMELINE.NO_USERS_VIEWED' | translate }}
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      }
    }
  }
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button mat-button [mat-dialog-close]="null" cdkFocusInitial translate="COMMON.DONE" color="primary"></button>
</mat-dialog-actions>
